import { useMemo, useState } from 'react';
import { Button } from '@/components/Button';
import { Select } from '@/components/Select';
import {
  type DataSafetyForm, type DataTypeUsage, type DataTypeDef, type PurposeId, type AccountMethodId, type OutsideAccountTypeId,
  DATA_CATEGORIES, PURPOSES, ACCOUNT_METHODS, OUTSIDE_ACCOUNT_TYPES, DELETION_OPTIONS,
  emptyUsage, fromCsv, toCsv, describeDataSafety,
} from '@/lib/data-safety';
import { Choice, YesNo, QuestionRow, CheckList, Group, SectionTitle, Note, Switch, Field, inputCls } from './controls';

interface Props { value: DataSafetyForm; onChange: (v: DataSafetyForm) => void }

const PURPOSE_OPTIONS = PURPOSES.map((p) => ({ value: p.id, label: p.label }));
const orderPurposes = (ids: PurposeId[]) => PURPOSES.map((p) => p.id).filter((id) => ids.includes(id));

function UsageEditor({ type, usage, onChange }: { type: DataTypeDef; usage: DataTypeUsage; onChange: (u: DataTypeUsage) => void }) {
  const set = (patch: Partial<DataTypeUsage>) => onChange({ ...usage, ...patch });
  const mode: 'collected' | 'shared' | 'both' | 'none' =
    usage.collected && usage.shared ? 'both' : usage.collected ? 'collected' : usage.shared ? 'shared' : 'none';
  return (
    <div className="mt-2 mb-3 ml-2 pl-4 border-l-2 border-primary/40 space-y-3">
      <div className="text-sm font-medium text-content">{type.label}</div>
      <QuestionRow label="Collected, shared, or both?" hint="Shared means sent to a third party, ad networks included.">
        <Choice<'collected' | 'shared' | 'both'>
          size="sm"
          value={mode === 'none' ? 'collected' : mode}
          onChange={(v) => set({ collected: v !== 'shared', shared: v !== 'collected' })}
          options={[{ value: 'collected', label: 'Collected' }, { value: 'shared', label: 'Shared' }, { value: 'both', label: 'Both' }]}
        />
      </QuestionRow>
      {usage.collected && (
        <>
          <QuestionRow label="Processed ephemerally" hint="Only held in memory, never stored longer than the request.">
            <YesNo size="sm" value={usage.ephemeral} onChange={(v) => set({ ephemeral: v })} />
          </QuestionRow>
          <QuestionRow label="Is this collection required?" hint="Or can users choose whether it is collected.">
            <Choice<'required' | 'optional'>
              size="sm"
              value={usage.required ? 'required' : 'optional'}
              onChange={(v) => set({ required: v === 'required' })}
              options={[{ value: 'required', label: 'Required' }, { value: 'optional', label: 'Optional' }]}
            />
          </QuestionRow>
          <div>
            <div className="text-sm text-content mb-1">Why is it collected?</div>
            <CheckList<PurposeId> columns={2} value={usage.collectionPurposes} options={PURPOSE_OPTIONS}
              onChange={(v) => set({ collectionPurposes: orderPurposes(v) })} />
          </div>
        </>
      )}
      {usage.shared && (
        <div>
          <div className="text-sm text-content mb-1">Why is it shared?</div>
          <CheckList<PurposeId> columns={2} value={usage.sharingPurposes} options={PURPOSE_OPTIONS}
            onChange={(v) => set({ sharingPurposes: orderPurposes(v) })} />
        </div>
      )}
    </div>
  );
}

function CsvTools({ value, onChange }: Props) {
  const [mode, setMode] = useState<'none' | 'import' | 'preview'>('none');
  const [pasted, setPasted] = useState('');
  const [report, setReport] = useState<string | null>(null);
  const csv = useMemo(() => (mode === 'preview' ? toCsv(value) : ''), [mode, value]);

  const load = () => {
    const { form, unknown } = fromCsv(pasted);
    onChange(form);
    setReport(unknown.length
      ? `Loaded. ${unknown.length} row${unknown.length === 1 ? '' : 's'} the form cannot hold were dropped: ${unknown.slice(0, 5).join(', ')}${unknown.length > 5 ? ', …' : ''}`
      : 'Loaded. Every row was understood.');
  };

  return (
    <div className="rounded-lg border border-dashed border-border p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-content-muted mr-auto">
          Saved as the CSV the Android Publisher API expects · {describeDataSafety(value)}
        </span>
        <Button size="sm" variant="secondary" onClick={() => { setMode(mode === 'import' ? 'none' : 'import'); setReport(null); }}>
          {mode === 'import' ? 'Close import' : 'Import console export'}
        </Button>
        <Button size="sm" variant="secondary" onClick={() => setMode(mode === 'preview' ? 'none' : 'preview')}>
          {mode === 'preview' ? 'Hide CSV' : 'Show CSV'}
        </Button>
      </div>
      {mode === 'import' && (
        <div className="mt-3 space-y-2">
          <textarea
            className={`${inputCls} font-mono text-xs h-32`}
            value={pasted}
            onChange={(e) => setPasted(e.target.value)}
            placeholder="Paste the file from Play Console → App content → Data safety → Export to CSV. It replaces every answer below."
          />
          <div className="flex items-center gap-3">
            <Button size="sm" onClick={load} disabled={!pasted.trim()}>Load into the form</Button>
            {report && <span className="text-xs text-content-muted">{report}</span>}
          </div>
        </div>
      )}
      {mode === 'preview' && (
        <textarea readOnly className={`${inputCls} font-mono text-xs h-48 mt-3`} value={csv} />
      )}
    </div>
  );
}

export function DataSafetySection({ value, onChange }: Props) {
  const set = <K extends keyof DataSafetyForm>(k: K, v: DataSafetyForm[K]) => onChange({ ...value, [k]: v });
  const hasAccounts = value.accountMethods.length > 0;

  const toggleType = (id: string) => {
    const types = { ...value.types };
    if (types[id]) delete types[id]; else types[id] = emptyUsage();
    set('types', types);
  };

  return (
    <div className="space-y-4">
      <SectionTitle sub="Policy → App content → Data safety. The only questionnaire the pipeline submits through the API, so this is what Google's review reads.">
        Data safety
      </SectionTitle>

      <CsvTools value={value} onChange={onChange} />

      <Group title="Overview">
        <QuestionRow
          label="Does the app collect or share any user data?"
          hint="Ad and analytics SDKs count: AdMob and Firebase collect device ids, interactions and crash logs."
        >
          <YesNo value={value.collectsOrShares} onChange={(v) => set('collectsOrShares', v)} />
        </QuestionRow>
        {!value.collectsOrShares && Object.keys(value.types).length === 0 && (
          <Note>
            An app that ships AdMob or Firebase and declares no data gets bounced by the console's quick
            checks ("Invalid data safety declaration"). Answer "Yes" unless the build really has no SDKs.
          </Note>
        )}
        {value.collectsOrShares && (
          <>
            <QuestionRow label="Is all collected data encrypted in transit?">
              <YesNo value={value.encryptedInTransit} onChange={(v) => set('encryptedInTransit', v)} />
            </QuestionRow>
            <Field label="Can users request that their data is deleted?">
              <Select value={value.deletion} onChange={(v) => set('deletion', v as DataSafetyForm['deletion'])}
                options={DELETION_OPTIONS.map((d) => ({ value: d.id, label: d.label }))} />
            </Field>
            {value.deletion === 'DATA_DELETION_YES' && (
              <Field label="Data deletion request URL">
                <input className={inputCls} type="url" value={value.dataDeletionUrl} onChange={(e) => set('dataDeletionUrl', e.target.value)} placeholder="https://" />
              </Field>
            )}
          </>
        )}
      </Group>

      {value.collectsOrShares && (
        <>
          <Group title="Accounts">
            <div>
              <div className="text-sm text-content mb-1.5">How can users create an account in the app?</div>
              <CheckList<AccountMethodId>
                value={value.accountMethods}
                onChange={(v) => set('accountMethods', ACCOUNT_METHODS.map((m) => m.id).filter((id) => v.includes(id)))}
                options={ACCOUNT_METHODS.map((m) => ({ value: m.id, label: m.label }))}
              />
              {!hasAccounts && <p className="text-xs text-content-muted mt-1 px-2">Nothing ticked means the app has no accounts.</p>}
            </div>
            {value.accountMethods.includes('PSL_ACM_OTHER') && (
              <Field label="Describe the other method">
                <input className={inputCls} value={value.accountMethodOther} onChange={(e) => set('accountMethodOther', e.target.value)} />
              </Field>
            )}
            {hasAccounts && (
              <Field label="Account deletion URL" hint="Required for any app with accounts.">
                <input className={inputCls} type="url" value={value.accountDeletionUrl} onChange={(e) => set('accountDeletionUrl', e.target.value)} placeholder="https://" />
              </Field>
            )}
            <Switch
              checked={value.outsideAppAccounts}
              onChange={(v) => set('outsideAppAccounts', v)}
              label="Users can sign in with accounts created outside the app"
              hint="SIM-bound, employer, or other externally issued accounts."
            />
            {value.outsideAppAccounts && (
              <div className="pl-4 border-l-2 border-border space-y-3">
                <CheckList<OutsideAccountTypeId>
                  value={value.outsideAccountTypes}
                  onChange={(v) => set('outsideAccountTypes', OUTSIDE_ACCOUNT_TYPES.map((t) => t.id).filter((id) => v.includes(id)))}
                  options={OUTSIDE_ACCOUNT_TYPES.map((t) => ({ value: t.id, label: t.label }))}
                />
                {value.outsideAccountTypes.includes('PSL_OUTSIDE_APP_ACCOUNT_TYPE_OTHER') && (
                  <Field label="Describe how these accounts are created">
                    <input className={inputCls} value={value.outsideAccountTypeOther} onChange={(e) => set('outsideAccountTypeOther', e.target.value)} />
                  </Field>
                )}
              </div>
            )}
          </Group>

          <Group title="Data types">
            <p className="text-sm text-content-muted -mt-1">
              Tick every type the app or its SDKs collect or share, then say how each one is used.
            </p>
            {DATA_CATEGORIES.map((cat) => {
              const declared = cat.types.filter((t) => value.types[t.id]);
              return (
                <div key={cat.id}>
                  <div className="text-xs font-medium text-content-subtle mb-1">{cat.label}</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-2">
                    {cat.types.map((t) => (
                      <label key={t.id} className="flex items-center gap-2.5 px-2 py-1.5 rounded-md cursor-pointer hover:bg-surface-muted">
                        <input type="checkbox" className="accent-primary" checked={!!value.types[t.id]} onChange={() => toggleType(t.id)} />
                        <span className="text-sm text-content">{t.label}</span>
                      </label>
                    ))}
                  </div>
                  {declared.map((t) => (
                    <UsageEditor key={t.id} type={t} usage={value.types[t.id]!} onChange={(u) => set('types', { ...value.types, [t.id]: u })} />
                  ))}
                </div>
              );
            })}
          </Group>

          <Group title="Optional">
            <Switch checked={value.compliesFamilyPolicy} onChange={(v) => set('compliesFamilyPolicy', v)}
              label="Complies with the Families policy" hint="Only meaningful for apps that target children." />
            <Switch checked={value.independentlyValidated} onChange={(v) => set('independentlyValidated', v)}
              label="Independently validated against a security standard (MASA)" />
            {value.independentlyValidated && (
              <Switch checked={value.upiBadge} onChange={(v) => set('upiBadge', v)} label="Show the badge on the store listing" />
            )}
          </Group>
        </>
      )}
    </div>
  );
}
